import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import DownloadFiles from './DownloadFiles'
import Can from '../../../../features/components/Can'
import UploadRequestedFiles from '../../../../forms/Multiplatform/MediaOrderForms/UploadRequestedFiles'
import RequestedFilesTable from './RequestedFilesTable'
import TableDataLoader from '../../../../components/Table/TableDataLoader'
import ContentRow from '../../../../features/components/ContentSection/ContentRow'
import ContentSection from '../../../../features/components/ContentSection'
import AddCommentsToUploadedFiles from '../../../../forms/Multiplatform/MediaOrderForms/AddCommentsToUploadedFiles'
import UpdateBookedMediaCustomDates from './UpdateBookedMediaCustomDates'

import {
  clearGetMediaOrderRequestedFiles,
  getMediaOrderRequestedFiles
} from '../../../../modules/actions/mediaOrdersBookings'
import { isAuthorizedSelector } from '../../../../modules/selectors/cognito'
import {
  getMediaOrderRequestedFilesIsLoadingSelector,
  getMediaOrderRequestedFilesErrorSelector,
  getMediaOrderRequestedFilesWasLoadedSelector,
  mediaOrderRequestedFilesByProductSelector
} from '../../../../modules/selectors/mediaOrdersBookings'

import { requestedFilesColumnsSchema } from './RequestedFilesTable/const/columns'
import { MEDIA_ORDER_CUSTOMISE_DATES_PERMISSION } from '../../../../constants/permissions'

import useStyles from './styles'
import useCommonStyles from '../../../../styles/common/listPage'

const RequestedFilesSection = ({ mediaOrderId }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const classes = useStyles()
  const commonClasses = useCommonStyles()

  const isAuthorized = useSelector(isAuthorizedSelector)
  const mediaOrderRequestedFilesByProduct = useSelector(mediaOrderRequestedFilesByProductSelector)

  const noDataContent = useMemo(
    () => (
      <div className={commonClasses.noDataContent}>
        <div>{t('There are no files')}</div>
      </div>
    ),
    [commonClasses.noDataContent, t]
  )

  useEffect(() => {
    dispatch(getMediaOrderRequestedFiles({ id: mediaOrderId }))

    return () => {
      dispatch(clearGetMediaOrderRequestedFiles())
    }
  }, [dispatch, mediaOrderId])

  return (
    <>
      <ContentSection title={t('Requested files:')}>
        <TableDataLoader
          itemsLength={mediaOrderRequestedFilesByProduct?.length}
          errorSelector={getMediaOrderRequestedFilesErrorSelector}
          wasLoadedSelector={getMediaOrderRequestedFilesWasLoadedSelector}
          isLoadingSelector={getMediaOrderRequestedFilesIsLoadingSelector}
          skeletonProps={{ cols: requestedFilesColumnsSchema }}
          noDataContent={noDataContent}
        >
          {mediaOrderRequestedFilesByProduct?.map(({ product, files }) => {
            const uploadedFilesCount = files.reduce((total, file) => {
              const uploadedFiles = file.uploaded_files || []
              return total + uploadedFiles.length
            }, 0)

            // For now, we agreed to update only first booked media, because it happens seldom when one product has multiple booked media
            const productFirstBookedMedia = files[0]?.booked_media?.id

            return (
              <div className={classes.product} key={product.id}>
                <ContentRow
                  title={product.name}
                  description={t('Please upload the requested files')}
                  leftColumnChildren={
                    // this page is used for the not authorized users as well, the download all api doesn't work with media order review token,
                    // so we need to check if the user is authorized
                    isAuthorized && uploadedFilesCount > 1 ? (
                      <DownloadFiles bookedMediaId={productFirstBookedMedia} productName={product.name} />
                    ) : null
                  }
                >
                  <RequestedFilesTable files={files} bookedMediaId={productFirstBookedMedia} />
                </ContentRow>
              </div>
            )
          })}
        </TableDataLoader>
      </ContentSection>
      <UploadRequestedFiles />
      <Can I="manage" a={MEDIA_ORDER_CUSTOMISE_DATES_PERMISSION}>
        <UpdateBookedMediaCustomDates mediaOrderId={mediaOrderId} />
      </Can>
      <AddCommentsToUploadedFiles />
    </>
  )
}

RequestedFilesSection.propTypes = {
  mediaOrderId: PropTypes.number.isRequired
}

export default RequestedFilesSection
